'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { saveWatchlists } from '@/lib/api-client';
import { addTickerAtTop } from '@/lib/watchlistPanel';

const LS_KEY = 'watchlists_v1';
const SYNC_EVENT = 'watchlist-updated';

const DEFAULT_TICKERS = ['NVDA', 'VTI', 'AMZN', 'MSFT', 'SPY', 'AAPL'];

export interface WatchItem { ticker: string; addedAt: number }
export interface WatchList { id: string; name: string; items: WatchItem[]; muted?: boolean }

function readLists(): WatchList[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as WatchList[];
  } catch { /* ignore */ }
  // First visit — seed with default tickers
  const now = Date.now();
  return [{ id: 'default', name: 'My Watchlist', items: DEFAULT_TICKERS.map((ticker, i) => ({ ticker, addedAt: now + i })) }];
}

function writeLists(lists: WatchList[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(lists));
  window.dispatchEvent(new Event(SYNC_EVENT));
}

export function useWatchlist() {
  const [lists, setLists] = useState<WatchList[]>([]);
  const [mounted, setMounted] = useState(false);
  const syncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLists(readLists());
    setMounted(true);
    const handler = () => setLists(readLists());
    window.addEventListener(SYNC_EVENT, handler);
    return () => window.removeEventListener(SYNC_EVENT, handler);
  }, []);

  // Debounced server sync
  const syncToServer = useCallback((updated: WatchList[]) => {
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      const dtos = updated.map((l, i) => ({
        id: l.id, name: l.name, pinned: false, muted: l.muted ?? false,
        items: l.items, sortOrder: i, updatedAt: '',
      }));
      saveWatchlists(dtos).catch(() => {});
    }, 500);
  }, []);

  const isInList = useCallback((listId: string, ticker: string) => {
    const list = lists.find((l) => l.id === listId);
    return list?.items.some((i) => i.ticker === ticker) ?? false;
  }, [lists]);

  // Muted lists are excluded from the global "is this ticker watched?"
  // check — that's the whole point of muting (no star indicator on the
  // stocks page / screener). Per-list checks via isInList still report
  // accurately so the "Add to list" picker doesn't silently double-add.
  const isWatched = useCallback((ticker: string) =>
    lists.some((l) => !l.muted && l.items.some((i) => i.ticker === ticker))
  , [lists]);

  const addToList = useCallback((listId: string, ticker: string) => {
    const next = readLists().map((l) =>
      l.id === listId
        ? { ...l, items: addTickerAtTop(l.items, ticker, Date.now()) }
        : l
    );
    writeLists(next);
    setLists(next);
    syncToServer(next);
  }, [syncToServer]);

  const removeFromList = useCallback((listId: string, ticker: string) => {
    const next = readLists().map((l) =>
      l.id === listId
        ? { ...l, items: l.items.filter((i) => i.ticker !== ticker) }
        : l
    );
    writeLists(next);
    setLists(next);
    syncToServer(next);
  }, [syncToServer]);

  const toggleInList = useCallback((listId: string, ticker: string) => {
    if (isInList(listId, ticker)) {
      removeFromList(listId, ticker);
    } else {
      addToList(listId, ticker);
    }
  }, [isInList, addToList, removeFromList]);

  return { lists, mounted, isWatched, isInList, addToList, removeFromList, toggleInList };
}
